
import { useState } from "react"
import { Link } from "react-router-dom"
import { Search, Filter, Plus, ArrowUpRight, Loader2, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useInvestors } from "@/lib/use-api"
import { createInvestor, type ApiInvestor } from "@/lib/api-client"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

const stageColors: Record<string, string> = {
  Lead: "bg-blue-100 text-blue-700",
  Active: "bg-emerald-100 text-emerald-700",
  "Meeting Scheduled": "bg-amber-100 text-amber-700",
  "Due Diligence": "bg-purple-100 text-purple-700",
  "Term Sheet": "bg-pink-100 text-pink-700",
  Closed: "bg-emerald-200 text-emerald-800",
  Portfolio: "bg-purple-100 text-purple-700",
  Passed: "bg-gray-100 text-gray-500",
}

const typeLabels: Record<string, string> = {
  "data-driven": "Data-Driven",
  "vision-focused": "Vision-Focused",
  "milestone-based": "Milestone-Based",
  "thesis-driven": "Thesis-Driven",
  opportunistic: "Opportunistic",
}

const stages = ["Lead", "Active", "Meeting Scheduled", "Due Diligence", "Portfolio", "Passed"] as const

function parseTags(tags: string | string[] | null): string[] {
  if (Array.isArray(tags)) return tags
  if (typeof tags === "string") {
    try {
      const parsed = JSON.parse(tags)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return tags ? tags.split(",").map((t) => t.trim()) : []
    }
  }
  return []
}

export default function InvestorsPage() {
  const { data: investors, isLoading, mutate } = useInvestors()
  const [search, setSearch] = useState("")
  const [stageFilter, setStageFilter] = useState<string>("All")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [newInvestor, setNewInvestor] = useState({
    name: "",
    firm: "",
    email: "",
    stage: "Lead",
    type: "data-driven",
    investment_thesis: "",
  })

  const filtered = (investors || []).filter((inv) => {
    const matchesSearch =
      inv.name.toLowerCase().includes(search.toLowerCase()) ||
      inv.firm.toLowerCase().includes(search.toLowerCase())
    const matchesStage = stageFilter === "All" || inv.stage === stageFilter
    return matchesSearch && matchesStage
  })

  async function handleCreate() {
    if (!newInvestor.name.trim() || !newInvestor.firm.trim()) return
    setIsCreating(true)
    try {
      await createInvestor({
        name: newInvestor.name,
        firm: newInvestor.firm,
        email: newInvestor.email || undefined,
        stage: newInvestor.stage,
        type: newInvestor.type,
        investment_thesis: newInvestor.investment_thesis || undefined,
      })
      await mutate()
      setDialogOpen(false)
      setNewInvestor({ name: "", firm: "", email: "", stage: "Lead", type: "data-driven", investment_thesis: "" })
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to create investor")
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Investors</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage and track all your investor relationships
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Add Investor
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Investor</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 mt-4">
              <div className="flex flex-col gap-2">
                <Label>Name *</Label>
                <Input
                  placeholder="e.g. Sarah Chen"
                  value={newInvestor.name}
                  onChange={(e) => setNewInvestor((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Firm *</Label>
                <Input
                  placeholder="e.g. Sequoia Capital"
                  value={newInvestor.firm}
                  onChange={(e) => setNewInvestor((p) => ({ ...p, firm: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="contact@firm.com"
                  value={newInvestor.email}
                  onChange={(e) => setNewInvestor((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Stage</Label>
                <select
                  value={newInvestor.stage}
                  onChange={(e) => setNewInvestor((p) => ({ ...p, stage: e.target.value }))}
                  className="rounded-lg border border-input bg-card px-3 py-2.5 text-sm"
                >
                  {stages.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <Label>Investment Thesis</Label>
                <Input
                  placeholder="Focused on B2B SaaS..."
                  value={newInvestor.investment_thesis}
                  onChange={(e) => setNewInvestor((p) => ({ ...p, investment_thesis: e.target.value }))}
                />
              </div>
              <Button onClick={handleCreate} disabled={isCreating || !newInvestor.name.trim() || !newInvestor.firm.trim()}>
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {isCreating ? "Creating..." : "Create Investor"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search investors or firms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-input bg-card py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <div className="flex gap-1 flex-wrap">
            <button
              type="button"
              onClick={() => setStageFilter("All")}
              className={cn(
                "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                stageFilter === "All"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              )}
            >
              All
            </button>
            {stages.map((stage) => (
              <button
                key={stage}
                type="button"
                onClick={() => setStageFilter(stage)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  stageFilter === stage
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-accent"
                )}
              >
                {stage}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Investor Cards Grid */}
      {isLoading ? (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((investor) => {
            const tags = parseTags(investor.tags)
            return (
              <Link
                key={investor.id}
                href={`/dashboard/investors/${investor.id}`}
                className="group glass-strong rounded-xl p-5 shadow-sm transition-all hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {investor.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                        {investor.name}
                      </p>
                      <p className="text-xs text-muted-foreground">{investor.firm}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </div>

                {investor.investment_thesis && (
                  <p className="mt-3 text-xs text-muted-foreground line-clamp-2">
                    {investor.investment_thesis}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-xs font-medium",
                      stageColors[investor.stage] || "bg-gray-100 text-gray-500"
                    )}
                  >
                    {investor.stage}
                  </span>
                  <div className="flex items-center gap-2">
                    {investor.type && (
                      <span className="text-xs text-muted-foreground">
                        {typeLabels[investor.type] || investor.type}
                      </span>
                    )}
                    <div className="h-3 w-px bg-border" />
                    <div className="flex items-center gap-1">
                      <div className="h-1.5 w-12 rounded-full bg-muted">
                        <div
                          className={cn(
                            "h-1.5 rounded-full",
                            investor.engagement_score >= 80
                              ? "bg-emerald-500"
                              : investor.engagement_score >= 50
                                ? "bg-amber-500"
                                : "bg-red-400"
                          )}
                          style={{ width: `${investor.engagement_score}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">
                        {investor.engagement_score}
                      </span>
                    </div>
                  </div>
                </div>

                {tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            )
          })}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className="mt-12 flex flex-col items-center justify-center text-center">
          <p className="text-sm text-muted-foreground">
            {investors && investors.length > 0
              ? "No investors found matching your criteria."
              : "No investors yet. Click \"Add Investor\" to get started."}
          </p>
        </div>
      )}
    </div>
  )
}
